// Rotinas — blocos ocupados por dia (usado pela Agenda e pelo Planner).
// Um evento pode atravessar a meia-noite: o excedente "transborda" pro dia seguinte.

import type { RoutineEvent, RoutineKind } from '@app-types/domain';
import { addDaysIso } from './dates';

export interface RoutineBlock {
  start_min: number; // dentro do dia (0..1440)
  end_min: number; // 0..1440
  routine: RoutineEvent;
  /** true se é o pedaço que transbordou do dia anterior */
  spill: boolean;
}

const MIN_DAY = 24 * 60;

function dowOf(dateIso: string): number {
  return new Date(`${dateIso}T12:00:00`).getDay();
}

function occursOn(r: RoutineEvent, dateIso: string): boolean {
  if (r.date) return r.date === dateIso;
  return (r.days_of_week ?? []).includes(dowOf(dateIso));
}

/** blocos de rotina que aparecem no dia (inclui transbordo do dia anterior) */
export function routineBlocksForDate(routines: RoutineEvent[], dateIso: string): RoutineBlock[] {
  const blocks: RoutineBlock[] = [];
  const prevIso = addDaysIso(dateIso, -1);

  for (const r of routines) {
    if (r.duration_min <= 0) continue;

    // ocorre neste dia: bloco [start, min(start+dur, 24h)]
    if (occursOn(r, dateIso)) {
      blocks.push({
        start_min: r.start_min,
        end_min: Math.min(MIN_DAY, r.start_min + r.duration_min),
        routine: r,
        spill: false,
      });
    }

    // ocorreu ontem e atravessou a meia-noite: bloco [0, excedente]
    if (occursOn(r, prevIso) && r.start_min + r.duration_min > MIN_DAY) {
      blocks.push({
        start_min: 0,
        end_min: Math.min(MIN_DAY, r.start_min + r.duration_min - MIN_DAY),
        routine: r,
        spill: true,
      });
    }
  }

  return blocks.sort((a, b) => a.start_min - b.start_min);
}

/** metadados visuais por tipo */
export const ROUTINE_KIND_META: Record<
  RoutineKind,
  { label: string; icon: string; color: string }
> = {
  sleep: { label: 'Sono', icon: '☾', color: '#7a5fff' },
  work: { label: 'Trabalho', icon: '⚒', color: '#ff7a40' },
  exercise: { label: 'Exercício', icon: '⚡', color: '#35d07f' },
  meal: { label: 'Refeição', icon: '◐', color: '#ffb000' },
  commitment: { label: 'Compromisso', icon: '◆', color: '#b14aff' },
  other: { label: 'Outro', icon: '▪', color: '#7a90b8' },
};
